import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Slider } from '../ui/slider';
import { Loader2, ListTree, Plus, X, Calendar, Clock, AlertTriangle, CheckCircle2, Hash, Folder, Pencil, Save, RotateCcw, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateShort, formatDateLong } from '../../lib/date_format';

const ActivityTable = ({ selectedWbsId, selectedWbs, selectedActivityId, onSelectActivity, onActivityChange }) => {
  const { activeSchedule, activeProject, can } = useProject();
  const { Action } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Activity Details panel state
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // Progress editing state
  const [editingProgress, setEditingProgress] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [progressForm, setProgressForm] = useState({
    actual_start: '',
    actual_finish: '',
    percent_complete: 0,
    remaining_duration: 0
  });
  
  // Inline-edit state for Actual Start / Actual Finish columns
  // editingCell shape: { activityId, field } | null
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Add Activity dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newActivity, setNewActivity] = useState({
    name: '',
    original_duration: 1,
    planned_start: '',
    planned_finish: '',
    status: 'not_started'
  });

  const fetchActivities = useCallback(async () => {
    if (!activeSchedule?.id) return;
    
    setLoading(true);
    try {
      let url = `/activities/schedule/${activeSchedule.id}`;
      if (selectedWbsId) {
        url += `?wbs_id=${selectedWbsId}&include_children=true`;
      }
      const response = await api.get(url);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSchedule?.id, selectedWbsId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Format helpers — delegated to timezone-safe shared utility.
  const formatDate = formatDateShort;
  const formatFullDate = formatDateLong;

  const getStatusBadge = (status, size = 'small') => {
    const config = {
      not_started: { label: 'Pending', className: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
      in_progress: { label: 'In Progress', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2 },
      completed: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    };
    const cfg = config[status] || config.not_started;
    
    if (size === 'large') {
      const Icon = cfg.icon;
      return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${cfg.className}`}>
          <Icon className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">{cfg.label}</span>
        </div>
      );
    }
    
    return (
      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 font-medium ${cfg.className}`}>
        {cfg.label}
      </Badge>
    );
  };

  // Handle row click - open details panel
  const handleRowClick = (activity) => {
    setSelectedActivity(activity);
    setShowDetailsPanel(true);
    onSelectActivity(activity);
    // Initialize progress form
    setProgressForm({
      actual_start: activity.actual_start ? activity.actual_start.split('T')[0] : '',
      actual_finish: activity.actual_finish ? activity.actual_finish.split('T')[0] : '',
      percent_complete: activity.percent_complete || 0,
      remaining_duration: activity.remaining_duration ?? activity.original_duration
    });
    setEditingProgress(false);
  };

  // Close details panel
  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedActivity(null);
    onSelectActivity(null);
    setEditingProgress(false);
  };
  
  // Format date for input
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };
  
  // Can edit progress / actual dates — COLLABORATE level on the active
  // project (EDIT_ACTUALS), mirroring backend core/rbac.py via lib/auth.js.
  const canEditProgress = () => {
    return can(Action.EDIT_ACTUALS);
  };

  // Can edit full-schedule fields (% Complete, Remaining Duration) — PLAN
  // (owner) level via EDIT_ACTIVITY. Per Roles_and_Permissions_Spec §7 these
  // are NOT part of the Collaborator "actuals only" write surface, so the
  // controls are hidden from collaborators (the backend /progress route also
  // rejects client-set progress below PLAN — defense in depth).
  const canEditScheduleFields = () => {
    return can(Action.EDIT_ACTIVITY);
  };
  
  // Start editing progress
  const handleStartEditProgress = () => {
    setProgressForm({
      actual_start: formatDateForInput(selectedActivity.actual_start),
      actual_finish: formatDateForInput(selectedActivity.actual_finish),
      percent_complete: selectedActivity.percent_complete || 0,
      remaining_duration: selectedActivity.remaining_duration ?? selectedActivity.original_duration
    });
    setEditingProgress(true);
  };
  
  // Cancel editing progress
  const handleCancelEditProgress = () => {
    setEditingProgress(false);
    setProgressForm({
      actual_start: formatDateForInput(selectedActivity.actual_start),
      actual_finish: formatDateForInput(selectedActivity.actual_finish),
      percent_complete: selectedActivity.percent_complete || 0,
      remaining_duration: selectedActivity.remaining_duration ?? selectedActivity.original_duration
    });
  };
  
  // Save progress update
  const handleSaveProgress = async () => {
    setSavingProgress(true);
    
    try {
      const payload = {};
      
      // Only include changed values
      if (progressForm.actual_start) {
        payload.actual_start = progressForm.actual_start;
      } else if (selectedActivity.actual_start && !progressForm.actual_start) {
        // Clearing actual_start
        payload.actual_start = null;
      }
      
      if (progressForm.actual_finish) {
        payload.actual_finish = progressForm.actual_finish;
      } else if (selectedActivity.actual_finish && !progressForm.actual_finish) {
        // Clearing actual_finish
        payload.actual_finish = null;
      }
      
      // % Complete / Remaining Duration are owner-only (spec §7); collaborators
      // never send them (the backend /progress route rejects them below PLAN).
      if (canEditScheduleFields()) {
        if (progressForm.percent_complete !== (selectedActivity.percent_complete || 0)) {
          payload.percent_complete = progressForm.percent_complete;
        }

        if (progressForm.remaining_duration !== (selectedActivity.remaining_duration ?? selectedActivity.original_duration)) {
          payload.remaining_duration = progressForm.remaining_duration;
        }
      }
      
      const response = await api.put(`/activities/${selectedActivity.id}/progress`, payload);
      
      toast.success('Progress updated');
      setSelectedActivity(response.data);
      setEditingProgress(false);
      
      // Refresh activities list
      await fetchActivities();
      
      // Notify parent of change (for CPM recalculation)
      if (onActivityChange) onActivityChange();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update progress';
      toast.error(message);
    } finally {
      setSavingProgress(false);
    }
  };

  // ── Inline edit: Actual Start / Actual Finish ────────────────────────────
  // Reuses PUT /activities/{id}/progress. Optimistic update + revert on error.
  const beginEditActual = (activity, field) => {
    const seed = activity[field] ? activity[field].split('T')[0] : '';
    setEditValue(seed);
    setEditingCell({ activityId: activity.id, field });
  };

  const cancelEditActual = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveActualDate = async (activity, field, rawValue) => {
    const value = rawValue || null; // empty string → null (clear date)

    // No-op if unchanged
    const current = activity[field] ? activity[field].split('T')[0] : null;
    if (current === value) {
      cancelEditActual();
      return;
    }

    // Snapshot for potential revert
    const previous = activities;

    // Optimistic update — patch just this field on this activity
    setActivities(prev => prev.map(a => (
      a.id === activity.id ? { ...a, [field]: value } : a
    )));
    setEditingCell(null);
    setEditValue('');

    try {
      const payload = { [field]: value };
      const response = await api.put(`/activities/${activity.id}/progress`, payload);

      // Replace with authoritative server copy (so derived fields like status,
      // percent_complete, remaining_duration stay in sync with backend rules)
      setActivities(prev => prev.map(a => (
        a.id === activity.id ? response.data : a
      )));

      toast.success('Updated');
      if (onActivityChange) onActivityChange();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update';
      toast.error(message);
      // Revert optimistic change, then refetch to guarantee clean state
      setActivities(previous);
      fetchActivities();
    }
  };

  const handleOpenAddDialog = () => {
    setNewActivity({
      name: '',
      original_duration: 1,
      planned_start: '',
      planned_finish: '',
      status: 'not_started'
    });
    setShowAddDialog(true);
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    
    if (!selectedWbsId) {
      toast.error('Please select a WBS element first');
      return;
    }
    
    if (!newActivity.name.trim()) {
      toast.error('Activity Name is required');
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        name: newActivity.name.trim(),
        schedule_id: activeSchedule.id,
        wbs_id: selectedWbsId,
        original_duration: parseInt(newActivity.original_duration) || 1,
        remaining_duration: parseInt(newActivity.original_duration) || 1,
        planned_start: newActivity.planned_start || null,
        planned_finish: newActivity.planned_finish || null,
        status: newActivity.status
      };

      await api.post('/activities', payload);
      toast.success('Activity created');
      setShowAddDialog(false);
      
      // Refresh activities
      await fetchActivities();
      
      // Notify parent of change (for CPM recalculation)
      if (onActivityChange) onActivityChange();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create activity';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white" data-testid="activity-table">
      {/* Header - Activities within Schedule */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Activities
          </h3>
          {selectedWbs && (
            <>
              <div className="h-4 w-px bg-slate-300" />
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400">in</span>
                <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                  {selectedWbs.code}
                </span>
                <span className="text-[10px] text-slate-500 max-w-[150px] truncate">
                  {selectedWbs.name}
                </span>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400">
            {activities.length} activit{activities.length !== 1 ? 'ies' : 'y'}
            {selectedWbsId && ' (filtered)'}
          </span>
          {can(Action.EDIT_ACTIVITY) && selectedWbsId && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={handleOpenAddDialog}
              data-testid="add-activity-button"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          )}
        </div>
      </div>
      
      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ListTree className="h-8 w-8 text-slate-300 mb-2" />
            <p className="text-sm text-slate-500 mb-2">
              {selectedWbsId ? 'No activities in selected WBS' : 'No activities'}
            </p>
            {can(Action.EDIT_ACTIVITY) && selectedWbsId && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={handleOpenAddDialog}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Activity
              </Button>
            )}
            {!selectedWbsId && (
              <p className="text-xs text-slate-400">Select a WBS to add activities</p>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-24 sticky top-0 bg-slate-50">
                  Activity ID
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide sticky top-0 bg-slate-50">
                  Activity Name
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-24 sticky top-0 bg-slate-50">
                  Plan Start
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-24 sticky top-0 bg-slate-50">
                  Plan Finish
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-24 sticky top-0 bg-slate-50">
                  Actual Start
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-24 sticky top-0 bg-slate-50">
                  Actual Finish
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-20 text-right sticky top-0 bg-slate-50">
                  Duration
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-20 text-right sticky top-0 bg-slate-50">
                  Total Float
                </TableHead>
                <TableHead className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide w-24 sticky top-0 bg-slate-50">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                // Inline Actual Start / Actual Finish editing — EDIT_ACTUALS
                // (COLLABORATE level on the active project).
                const canEdit = can(Action.EDIT_ACTUALS);
                // Today (yyyy-mm-dd) for the date input max attribute
                const today = new Date().toISOString().split('T')[0];
                const dataDate = activeProject?.data_date
                  ? activeProject.data_date.split('T')[0]
                  : null;
                // Use the smaller (earlier) of today vs data_date so users
                // cannot record an actual that's after the project's data date.
                const maxActualDate = dataDate && dataDate < today ? dataDate : today;
                return activities.map((activity) => {
                  const isSelected = selectedActivityId === activity.id;
                  const isCritical = activity.total_float === 0 || activity.is_critical;
                  const isEditingStart = editingCell?.activityId === activity.id && editingCell?.field === 'actual_start';
                  const isEditingFinish = editingCell?.activityId === activity.id && editingCell?.field === 'actual_finish';
                  return (
                  <TableRow
                    key={activity.id}
                    className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 hover:bg-blue-50' : isCritical ? 'bg-amber-50/30 hover:bg-amber-50/50' : 'hover:bg-slate-50'}`}
                    onClick={() => handleRowClick(activity)}
                    data-testid={`activity-row-${activity.id}`}
                  >
                    <TableCell className={`font-mono text-xs ${isCritical ? 'text-amber-700 font-medium' : 'text-slate-600'}`}>
                      {activity.activity_id}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className={`text-xs ${isCritical ? 'text-amber-900' : 'text-slate-900'}`}>{activity.name}</p>
                        {activity.wbs_code && (
                          <p className="text-[10px] text-slate-400">{activity.wbs_code}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 tabular-nums">
                      {formatDate(activity.planned_start || activity.early_start)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 tabular-nums">
                      {formatDate(activity.planned_finish || activity.early_finish)}
                    </TableCell>
                    {/* Actual Start — inline editable for scheduler/superintendent */}
                    <TableCell
                      className="text-xs text-slate-600 tabular-nums"
                      data-testid={`actual-start-cell-${activity.id}`}
                      onClick={canEdit && !isEditingStart ? (e) => {
                        // Don't bubble: opening the row Sheet would clobber edit intent
                        e.stopPropagation();
                        beginEditActual(activity, 'actual_start');
                      } : undefined}
                    >
                      {isEditingStart ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Input
                            type="date"
                            value={editValue}
                            max={maxActualDate}
                            autoFocus
                            className="h-7 text-xs px-1"
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => saveActualDate(activity, 'actual_start', editValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveActualDate(activity, 'actual_start', editValue);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEditActual();
                              }
                              // Tab: let default behavior run; onBlur will save
                            }}
                          />
                        </div>
                      ) : (
                        formatDate(activity.actual_start)
                      )}
                    </TableCell>
                    {/* Actual Finish — disabled until Actual Start is set (mirrors backend) */}
                    <TableCell
                      className="text-xs text-slate-600 tabular-nums"
                      data-testid={`actual-finish-cell-${activity.id}`}
                      onClick={canEdit && !isEditingFinish && activity.actual_start ? (e) => {
                        e.stopPropagation();
                        beginEditActual(activity, 'actual_finish');
                      } : undefined}
                    >
                      {isEditingFinish ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Input
                            type="date"
                            value={editValue}
                            min={activity.actual_start ? activity.actual_start.split('T')[0] : undefined}
                            max={maxActualDate}
                            disabled={!activity.actual_start}
                            autoFocus
                            className="h-7 text-xs px-1"
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => saveActualDate(activity, 'actual_finish', editValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveActualDate(activity, 'actual_finish', editValue);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEditActual();
                              }
                            }}
                          />
                        </div>
                      ) : (
                        formatDate(activity.actual_finish)
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 tabular-nums text-right">
                      {activity.original_duration}d
                    </TableCell>
                    <TableCell className={`text-xs tabular-nums text-right ${isCritical ? 'text-amber-600 font-medium' : 'text-slate-600'}`}>
                      {activity.total_float !== null ? `${activity.total_float}d` : '—'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(activity.status)}
                    </TableCell>
                  </TableRow>
                );
                });
              })()}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Activity Details Panel (Sheet) */}
      <Sheet open={showDetailsPanel} onOpenChange={setShowDetailsPanel}>
        <SheetContent className="w-[400px] sm:w-[450px] p-0" data-testid="activity-details-panel">
          {selectedActivity && (
            <>
              <SheetHeader className="p-5 pb-4 border-b border-slate-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <SheetTitle className="text-base font-semibold text-slate-900 mb-1">
                      Activity Details
                    </SheetTitle>
                    <SheetDescription className="text-xs text-slate-500">
                      View information about this activity
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              
              <div className="p-5 space-y-5 overflow-auto max-h-[calc(100vh-120px)]">
                {/* Activity ID & Name */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <Hash className="h-4 w-4 text-slate-400" />
                    <span className="font-mono text-sm font-medium text-slate-700">
                      {selectedActivity.activity_id}
                    </span>
                    {(selectedActivity.total_float === 0 || selectedActivity.is_critical) && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 ml-auto text-[10px]">
                        Critical
                      </Badge>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                      Activity Name
                    </label>
                    <p className="text-sm font-medium text-slate-900 mt-1">
                      {selectedActivity.name}
                    </p>
                  </div>
                </div>
                
                {/* Status */}
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                    Status
                  </label>
                  <div className="mt-1.5">
                    {getStatusBadge(selectedActivity.status, 'large')}
                  </div>
                </div>
                
                {/* WBS */}
                {(selectedActivity.wbs_code || selectedActivity.wbs_name) && (
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                      WBS Element
                    </label>
                    <div className="flex items-center gap-2 mt-1.5 p-2 bg-slate-50 rounded">
                      <Folder className="h-4 w-4 text-amber-500" />
                      <div>
                        {selectedActivity.wbs_code && (
                          <span className="text-xs font-mono text-slate-600 mr-2">
                            {selectedActivity.wbs_code}
                          </span>
                        )}
                        {selectedActivity.wbs_name && (
                          <span className="text-xs text-slate-700">
                            {selectedActivity.wbs_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Duration */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                      Original Duration
                    </label>
                    <p className="text-sm font-medium text-slate-900 mt-1">
                      {selectedActivity.original_duration} day{selectedActivity.original_duration !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                      Remaining Duration
                    </label>
                    <p className="text-sm font-medium text-slate-900 mt-1">
                      {selectedActivity.remaining_duration ?? selectedActivity.original_duration} day{(selectedActivity.remaining_duration ?? selectedActivity.original_duration) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                
                {/* Dates Section */}
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Schedule Dates
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Planned Dates */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                          Planned Start
                        </label>
                        <p className="text-xs text-slate-700 mt-1">
                          {formatFullDate(selectedActivity.planned_start)}
                        </p>
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                          Planned Finish
                        </label>
                        <p className="text-xs text-slate-700 mt-1">
                          {formatFullDate(selectedActivity.planned_finish)}
                        </p>
                      </div>
                    </div>
                    
                    {/* CPM Calculated Dates */}
                    {(selectedActivity.early_start || selectedActivity.early_finish) && (
                      <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                        <p className="text-[10px] font-medium text-blue-600 uppercase tracking-wide mb-2">
                          CPM Calculated
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-[10px] text-slate-500">Early Start</label>
                            <p className="text-xs text-slate-700">{formatFullDate(selectedActivity.early_start)}</p>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500">Early Finish</label>
                            <p className="text-xs text-slate-700">{formatFullDate(selectedActivity.early_finish)}</p>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500">Late Start</label>
                            <p className="text-xs text-slate-700">{formatFullDate(selectedActivity.late_start)}</p>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500">Late Finish</label>
                            <p className="text-xs text-slate-700">{formatFullDate(selectedActivity.late_finish)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Actual Dates */}
                    {(selectedActivity.actual_start || selectedActivity.actual_finish) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                            Actual Start
                          </label>
                          <p className="text-xs text-slate-700 mt-1">
                            {formatFullDate(selectedActivity.actual_start)}
                          </p>
                        </div>
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                            Actual Finish
                          </label>
                          <p className="text-xs text-slate-700 mt-1">
                            {formatFullDate(selectedActivity.actual_finish)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Float Section */}
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Float Analysis
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${selectedActivity.total_float === 0 || selectedActivity.is_critical ? 'bg-amber-50 border border-amber-100' : 'bg-slate-50'}`}>
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                        Total Float
                      </label>
                      <p className={`text-lg font-semibold mt-1 ${selectedActivity.total_float === 0 || selectedActivity.is_critical ? 'text-amber-600' : 'text-slate-900'}`}>
                        {selectedActivity.total_float !== null ? `${selectedActivity.total_float}d` : '—'}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                        Free Float
                      </label>
                      <p className="text-lg font-semibold text-slate-900 mt-1">
                        {selectedActivity.free_float !== null ? `${selectedActivity.free_float}d` : '—'}
                      </p>
                    </div>
                  </div>
                  
                  {(selectedActivity.total_float === 0 || selectedActivity.is_critical) && (
                    <div className="mt-3 p-2 bg-amber-50 border border-amber-100 rounded flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        This activity is on the critical path. Any delay will directly impact the project finish date.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Progress & Actuals Section */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                      <TrendingUp className="h-3.5 w-3.5" />
                      Progress & Actuals
                    </h4>
                    {canEditProgress() && !editingProgress && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs gap-1"
                        onClick={handleStartEditProgress}
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </Button>
                    )}
                  </div>
                  
                  {editingProgress ? (
                    /* Edit Mode */
                    <div className="space-y-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      {/* Actual Dates */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] font-medium text-slate-500 uppercase">
                            Actual Start
                          </Label>
                          <Input
                            type="date"
                            value={progressForm.actual_start}
                            onChange={(e) => setProgressForm({ ...progressForm, actual_start: e.target.value })}
                            className="h-8 text-sm mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] font-medium text-slate-500 uppercase">
                            Actual Finish
                          </Label>
                          <Input
                            type="date"
                            value={progressForm.actual_finish}
                            onChange={(e) => setProgressForm({ ...progressForm, actual_finish: e.target.value })}
                            className="h-8 text-sm mt-1"
                            min={progressForm.actual_start || undefined}
                            disabled={!progressForm.actual_start}
                          />
                          {!progressForm.actual_start && progressForm.actual_finish === '' && (
                            <p className="text-[10px] text-slate-400 mt-0.5">Set Actual Start first</p>
                          )}
                        </div>
                      </div>
                      
                      {/* % Complete + Remaining Duration — owner-only (full-schedule
                          fields, not the Collaborator actuals surface, spec §7). */}
                      {canEditScheduleFields() && (
                      <>
                      {/* Percent Complete */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-[10px] font-medium text-slate-500 uppercase">
                            % Complete
                          </Label>
                          <span className="text-sm font-medium text-slate-900">{progressForm.percent_complete}%</span>
                        </div>
                        <Slider
                          value={[progressForm.percent_complete]}
                          onValueChange={(value) => setProgressForm({ ...progressForm, percent_complete: value[0] })}
                          max={100}
                          step={5}
                          className="w-full"
                          disabled={progressForm.actual_finish !== ''}
                        />
                        {progressForm.actual_finish && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Auto-set to 100% when Actual Finish is entered</p>
                        )}
                      </div>

                      {/* Remaining Duration */}
                      <div>
                        <Label className="text-[10px] font-medium text-slate-500 uppercase">
                          Remaining Duration (days)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={progressForm.remaining_duration}
                          onChange={(e) => setProgressForm({ ...progressForm, remaining_duration: parseInt(e.target.value) || 0 })}
                          className="h-8 text-sm mt-1 w-24"
                          disabled={progressForm.actual_finish !== ''}
                        />
                        {progressForm.actual_finish && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Auto-set to 0 when finished</p>
                        )}
                      </div>
                      </>
                      )}
                      
                      {/* Status Preview */}
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                        <span className="text-[10px] font-medium text-slate-500 uppercase">Status:</span>
                        {progressForm.actual_finish ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Completed</Badge>
                        ) : progressForm.actual_start ? (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">In Progress</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 text-[10px]">Not Started</Badge>
                        )}
                        <span className="text-[10px] text-slate-400 ml-auto">(auto-determined)</span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1"
                          onClick={handleCancelEditProgress}
                          disabled={savingProgress}
                        >
                          <RotateCcw className="h-3 w-3" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-xs gap-1 bg-slate-900 hover:bg-slate-800"
                          onClick={handleSaveProgress}
                          disabled={savingProgress}
                        >
                          {savingProgress ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Save className="h-3 w-3" />
                          )}
                          Save Progress
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="space-y-3">
                      {/* Progress Bar */}
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">% Complete</span>
                          <span className="font-medium text-slate-900">{selectedActivity.percent_complete || 0}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              selectedActivity.status === 'completed' ? 'bg-emerald-500' :
                              selectedActivity.status === 'in_progress' ? 'bg-blue-500' :
                              'bg-slate-300'
                            }`}
                            style={{ width: `${selectedActivity.percent_complete || 0}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Actual Dates */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                            Actual Start
                          </label>
                          <p className="text-xs text-slate-700 mt-1">
                            {selectedActivity.actual_start ? formatFullDate(selectedActivity.actual_start) : '—'}
                          </p>
                        </div>
                        <div>
                          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                            Actual Finish
                          </label>
                          <p className="text-xs text-slate-700 mt-1">
                            {selectedActivity.actual_finish ? formatFullDate(selectedActivity.actual_finish) : '—'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Remaining Duration */}
                      <div>
                        <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                          Remaining Duration
                        </label>
                        <p className="text-xs text-slate-700 mt-1">
                          {selectedActivity.remaining_duration ?? selectedActivity.original_duration} day{(selectedActivity.remaining_duration ?? selectedActivity.original_duration) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={handleCloseDetails}
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Activity Dialog - NO Activity ID field */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">Add Activity</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Create a new activity under {selectedWbs?.name || 'selected WBS'}. Activity ID will be auto-generated.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateActivity}>
            <div className="space-y-3 py-3">
              {/* Activity Name - Full Width */}
              <div className="space-y-1.5">
                <Label htmlFor="activity-name" className="text-xs font-medium">
                  Activity Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="activity-name"
                  placeholder="Enter activity name"
                  value={newActivity.name}
                  onChange={(e) => setNewActivity({ ...newActivity, name: e.target.value })}
                  className="h-9 text-sm"
                  data-testid="activity-name-input"
                  autoFocus
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="duration" className="text-xs font-medium">
                    Duration (days)
                  </Label>
                  <Input
                    id="duration"
                    type="number"
                    min="0"
                    value={newActivity.original_duration}
                    onChange={(e) => setNewActivity({ ...newActivity, original_duration: e.target.value })}
                    className="h-8 text-sm"
                    data-testid="activity-duration-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="planned-start" className="text-xs font-medium">
                    Planned Start
                  </Label>
                  <Input
                    id="planned-start"
                    type="date"
                    value={newActivity.planned_start}
                    onChange={(e) => setNewActivity({ ...newActivity, planned_start: e.target.value })}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="planned-finish" className="text-xs font-medium">
                    Planned Finish
                  </Label>
                  <Input
                    id="planned-finish"
                    type="date"
                    value={newActivity.planned_finish}
                    onChange={(e) => setNewActivity({ ...newActivity, planned_finish: e.target.value })}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="status" className="text-xs font-medium">
                  Status
                </Label>
                <Select 
                  value={newActivity.status} 
                  onValueChange={(value) => setNewActivity({ ...newActivity, status: value })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddDialog(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={isCreating}
                data-testid="create-activity-submit"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Activity'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivityTable;
